/**
 * useNavigation - Navigation hook
 *
 * Wraps React Router's useNavigate and useLocation
 * to provide view-based navigation matching the bottom nav tabs.
 */

import { useNavigate, useLocation } from 'react-router-dom'
import { useCallback, useMemo } from 'react'

export type ViewId = 'generate' | 'review' | 'library' | 'import' | 'media' | 'mediaNew' | 'stats' | 'settings'

const VIEW_PATHS: Record<ViewId, string> = {
  generate: '/app',
  review: '/app/review',
  library: '/app/library',
  import: '/app/import',
  media: '/app/media',
  mediaNew: '/app/media/new',
  stats: '/app/stats',
  settings: '/app/settings',
}

const PATH_TO_VIEW: Record<string, ViewId> = {
  '/app': 'generate',
  '/app/': 'generate',
  '/app/review': 'review',
  '/app/library': 'library',
  '/app/import': 'import',
  '/app/media': 'media',
  '/app/media/new': 'mediaNew',
  '/app/stats': 'stats',
  '/app/settings': 'settings',
}

export interface UseNavigationReturn {
  currentView: ViewId
  navigateTo: (view: ViewId) => void
  isView: (view: ViewId) => boolean
}

export function useNavigation(): UseNavigationReturn {
  const navigate = useNavigate()
  const location = useLocation()

  const currentView = useMemo<ViewId>(() => {
    return PATH_TO_VIEW[location.pathname] ?? 'generate'
  }, [location.pathname])

  const navigateTo = useCallback((view: ViewId) => {
    navigate(VIEW_PATHS[view])
  }, [navigate])

  const isView = useCallback((view: ViewId) => {
    return currentView === view
  }, [currentView])

  return { currentView, navigateTo, isView }
}
